/**
 * Página com loading e progresso sequencial
 * Orquestra as 8 chamadas de API e exibe progresso visual
 * Segue EXATAMENTE as mesmas APIs que o usuário usa no admin frontend
 * NOTA: Atualização de tema é feita quando o usuário seleciona o tema (ThemeSelectorPage)
 */

import { Box, Container, Typography, LinearProgress, Stack, Alert } from '@mui/material';
import { useEffect, useState } from 'react';
import OnboardingService from '../../services/onboardingService';
import { authService } from '../../services/authService';

const PROCESSING_STEPS = [
  'Criando Profissional',
  'Criando Serviço',
  'Criando Local',
  'Vinculando Serviço ao Local',
  'Vinculando Profissional ao Local',
  'Vinculando Profissional ao Serviço',
  'Criando Agenda',
  'Criando Turnos',
];

export default function ProcessingPage({ onboarding, template, responses }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isProcessing, setIsProcessing] = useState(true);
  const [processingError, setProcessingError] = useState(null);

  // Detectar se está em modo DEV
  const isDevelopment = import.meta.env.DEV;

  useEffect(() => {
    const processOnboarding = async () => {
      try {
        // Obter token do authService (salvo automaticamente pelo App)
        const token = authService.getToken();
        if (!token) {
          throw new Error('Token de autenticação não encontrado. Faça login novamente.');
        }

        console.log('[ProcessingPage] Token obtido, iniciando processamento');
        const service = new OnboardingService(token);

        // Callback para atualizar apenas os 8 passos principais, não as chamadas internas
        const onStepComplete = (stepIndex) => {
          setCurrentStep(stepIndex + 1);
          console.log('[ProcessingPage] Passo concluído:', stepIndex + 1, 'de', PROCESSING_STEPS.length);
        };

        // Executar as 8 etapas sequencialmente com callback de progresso
        const result = await service.createOnboardingStructure(responses, template, onStepComplete);

        // Sucesso!
        setIsProcessing(false);
        onboarding.completeOnboarding(result);
      } catch (error) {
        console.error('[ProcessingPage] Erro no processamento:', error);
        const errorMessage = error.message || 'Erro desconhecido no processamento';
        
        if (isDevelopment) {
          // Em DEV: exibir modal de erro
          setProcessingError(errorMessage);
          setIsProcessing(false);
          onboarding.setError(errorMessage);
        } else {
          // Em PROD: apenas log no console e ir para admin com estado atual
          console.error('[ProcessingPage] PROD MODE - Erro não exibido ao usuário. Redirecionando para admin...');
          setIsProcessing(false);
          // Marcar como completado mesmo com erro, para que possa ir ao admin
          onboarding.completeOnboarding({
            success: false,
            createdIds: onboarding.createdIds || {},
            publicLink: '',
            entities: onboarding.result || {},
          });
        }
      }
    };

    // Iniciar processamento (sem interval de progresso)
    processOnboarding();
  }, [responses, template, onboarding]);

  const progressPercent = ((currentStep + 1) / PROCESSING_STEPS.length) * 100;

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#ffffff',
        padding: { xs: '32px 16px', md: '48px 32px' },
        boxSizing: 'border-box',
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4, maxWidth: 700, width: '100%' }}>
        <Typography
          variant="h4"
          sx={{
            color: '#1a1a1a',
            fontWeight: 700,
            mb: 1,
            fontSize: { xs: '1.5rem', md: '1.75rem' },
          }}
        >
          Criando sua Estrutura
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: '#666666',
            fontWeight: 400,
            fontSize: { xs: '0.9rem', md: '0.95rem' },
          }}
        >
          Aguarde enquanto preparamos tudo para você
        </Typography>
      </Box>

      {/* Processing Container */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          maxWidth: 600,
        }}
      >
        <Box sx={{ width: '100%' }}>
          {/* Progress Bar */}
          <Box sx={{ mb: 3 }}>
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{
                height: 4,
                borderRadius: '2px',
                backgroundColor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#1a1a1a',
                },
              }}
            />
            <Typography
              sx={{
                color: '#999999',
                fontSize: '0.8rem',
                mt: 1,
                textAlign: 'right',
              }}
            >
              Etapa {currentStep + 1} de {PROCESSING_STEPS.length}
            </Typography>
          </Box>

          {/* Processing Steps */}
          <Box sx={{ space: 1.5 }}>
            {PROCESSING_STEPS.map((step, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  padding: '12px 0',
                  marginBottom: '12px',
                  transition: 'all 0.3s ease',
                }}
              >
                {/* Status Icon */}
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor:
                      index < currentStep
                        ? '#4caf50'
                        : index === currentStep
                        ? '#1a1a1a'
                        : '#e0e0e0',
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    flexShrink: 0,
                  }}
                >
                  {index < currentStep ? '✓' : index === currentStep ? '⟳' : (index + 1)}
                </Box>

                {/* Step Text */}
                <Typography
                  sx={{
                    color:
                      index <= currentStep
                        ? '#1a1a1a'
                        : '#999999',
                    fontWeight: index === currentStep ? 600 : 500,
                    fontSize: '0.95rem',
                  }}
                >
                  {step}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Error Alert (se houver) */}
          {processingError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Erro no processamento:</strong>
              </Typography>
              <Typography variant="body2">{processingError}</Typography>
            </Alert>
          )}

          {/* Status Message */}
          <Typography
            sx={{
              textAlign: 'center',
              color: '#666666',
              fontSize: '0.9rem',
              mt: 3,
            }}
          >
            {isProcessing ? 'Processando...' : 'Concluído!'}
          </Typography>
        </Box>
      </Box>

      {/* Footer */}
    </Box>
  );
}
